import React, { useState, useEffect,useCallback } from "react";
import { db } from "../firebase";
import {
  collection,
  getDocs,
  updateDoc,
  deleteDoc,
  doc,
  query,
  orderBy,
} from "firebase/firestore";

/* ─── SAME HOME THEME TOKENS ─── */
const THEMES = {
  light: {
    bg: "#ffffff",
    bgSub: "#f7f7f8",
    surface: "#f0f0f2",
    surfaceHover: "#e8e8eb",
    border: "#e3e3e6",
    borderStrong: "#c5c5ca",
    text: "#111111",
    textSub: "#5f5f6b",
    textMuted: "#a3a3b0",
    userBubble: "#1a1a1a",
    userText: "#ffffff",
    aiBubble: "#f7f7f8",
    aiBorder: "#e8e8eb",
    aiText: "#111111",
    accent: "#1a1a1a",
    accentText: "#ffffff",
    danger: "#d93025",
    success: "#188038",
    inputBg: "#f7f7f8",
    inputBorder: "#e3e3e6",
    sidebarBg: "#f7f7f8",
    sidebarBorder: "#e3e3e6",
    pillActive: "#1a1a1a",
    pillActiveText: "#ffffff",
    pillBg: "#ebebed",
    pillText: "#5f5f6b",
    shadow: "0 1px 3px rgba(0,0,0,0.07), 0 1px 2px rgba(0,0,0,0.05)",
    shadowMd: "0 4px 20px rgba(0,0,0,0.08)",
  },
  dark: {
    bg: "#212121",
    bgSub: "#2a2a2a",
    surface: "#303030",
    surfaceHover: "#3a3a3a",
    border: "#3a3a3a",
    borderStrong: "#4e4e4e",
    text: "#ececec",
    textSub: "#9e9e9e",
    textMuted: "#616161",
    userBubble: "#f0f0f0",
    userText: "#111111",
    aiBubble: "#2a2a2a",
    aiBorder: "#3a3a3a",
    aiText: "#ececec",
    accent: "#ececec",
    accentText: "#111111",
    danger: "#f28b82",
    success: "#81c995",
    inputBg: "#2a2a2a",
    inputBorder: "#3a3a3a",
    sidebarBg: "#1a1a1a",
    sidebarBorder: "#2e2e2e",
    pillActive: "#ececec",
    pillActiveText: "#111111",
    pillBg: "#303030",
    pillText: "#9e9e9e",
    shadow: "0 1px 3px rgba(0,0,0,0.3)",
    shadowMd: "0 4px 20px rgba(0,0,0,0.35)",
  },
};

const credentials = {
  omesh: "omesh123",
  liron: "Gooner19",
  ray: "Rayyan123",
  admin:"omrali123*"
};

export default function AdminPage() {
  const [loggedIn, setLoggedIn] = useState(false);
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [activeTab, setActiveTab] = useState("history");

  const [conversations, setConversations] = useState([]);
  const [users, setUsers] = useState([]);
  const [waitlist, setWaitlist] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [views, setViews] = useState([]);
  const [donors, setDonors] = useState([]);

  const [loading, setLoading] = useState(false);

  // CUSTOM ALERT / MODAL STATES
  const [alertModal, setAlertModal] = useState({
    open: false,
    title: "",
    message: "",
    type: "info", // info | success | error
  });

  const [confirmModal, setConfirmModal] = useState({
    open: false,
    title: "",
    message: "",
    onConfirm: null,
  });

  const darkMode = localStorage.getItem("darkMode") === "true";
  const T = darkMode ? THEMES.dark : THEMES.light;

  const showAlert = (title, message, type = "info") => {
    setAlertModal({
      open: true,
      title,
      message,
      type,
    });
  };

  const closeAlert = () => {
    setAlertModal({
      open: false,
      title: "",
      message: "",
      type: "info",
    });
  };

  const showConfirm = (title, message, onConfirm) => {
    setConfirmModal({
      open: true,
      title,
      message,
      onConfirm,
    });
  };

  const closeConfirm = () => {
    setConfirmModal({
      open: false,
      title: "",
      message: "",
      onConfirm: null,
    });
  };

  const handleLogin = () => {
    if (credentials[username] && credentials[username] === password) {
      setLoggedIn(true);
    } else {
      showAlert(
        "Login failed",
        "Invalid username or password. Please check your credentials and try again.",
        "error"
      );
    }
  };

  const fetchData = useCallback(async () => {
  setLoading(true);
  try {
    const convSnap = await getDocs(
      query(collection(db, "conversations"), orderBy("timestamp", "desc"))
    );
    setConversations(convSnap.docs.map((d) => ({ id: d.id, ...d.data() })));

    const usersSnap = await getDocs(collection(db, "users"));
    setUsers(usersSnap.docs.map((d) => ({ id: d.id, ...d.data() })));

    const waitSnap = await getDocs(collection(db, "waitlist"));
    setWaitlist(waitSnap.docs.map((d) => ({ id: d.id, ...d.data() })));

    const contactSnap = await getDocs(collection(db, "contact"));
    setContacts(contactSnap.docs.map((d) => ({ id: d.id, ...d.data() })));

    const viewSnap = await getDocs(
      query(collection(db, "views"), orderBy("timestamp", "desc"))
    );
    setViews(viewSnap.docs.map((d) => ({ id: d.id, ...d.data() })));

    const donorSnap = await getDocs(
      query(collection(db, "donors"), orderBy("timestamp", "desc"))
    );
    setDonors(donorSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
  } catch (error) {
    console.error("Error fetching data: ", error);
    showAlert(
      "Refresh failed",
      "Could not fetch dashboard data from Firebase.",
      "error"
    );
  } finally {
    setLoading(false);
  }
}, []);

  useEffect(() => {
  if (loggedIn) {
    fetchData();
  }
}, [loggedIn, fetchData]);

  const markContactChecked = async (id) => {
    try {
      const contactRef = doc(db, "contact", id);
      await updateDoc(contactRef, { checked: true });
      setContacts((prev) =>
        prev.map((c) => (c.id === id ? { ...c, checked: true } : c))
      );
      showAlert(
        "Marked as checked",
        "The contact message has been marked as checked.",
        "success"
      );
    } catch (error) {
      console.error("Error marking contact checked:", error);
      showAlert(
        "Action failed",
        "Could not mark this contact message as checked.",
        "error"
      );
    }
  };

  const handleDelete = async (
    collectionName,
    id,
    stateSetter,
    itemName = "item"
  ) => {
    showConfirm(
      "Delete confirmation",
      `Are you sure you want to delete this ${itemName}? This action cannot be undone.`,
      async () => {
        try {
          await deleteDoc(doc(db, collectionName, id));
          stateSetter((prev) => prev.filter((item) => item.id !== id));
          showAlert(
            "Deleted successfully",
            `The ${itemName} has been deleted successfully.`,
            "success"
          );
        } catch (error) {
          console.error(`Error deleting ${itemName}:`, error);
          showAlert(
            "Delete failed",
            `Failed to delete this ${itemName}. Please try again.`,
            "error"
          );
        } finally {
          closeConfirm();
        }
      }
    );
  };

  const formatDate = (ts) => {
    try {
      return ts?.toDate?.().toLocaleString() || "No timestamp";
    } catch {
      return "No timestamp";
    }
  };

  const Icon = ({ children, style = {} }) => (
    <span
      className="material-icons-round"
      style={{ fontSize: 20, lineHeight: 1, ...style }}
    >
      {children}
    </span>
  );

  const tabButton = (key, label) => (
    <button
      key={key}
      onClick={() => setActiveTab(key)}
      style={{
        padding: "9px 14px",
        borderRadius: 999,
        border: `1px solid ${activeTab === key ? T.pillActive : T.border}`,
        background: activeTab === key ? T.pillActive : T.pillBg,
        color: activeTab === key ? T.pillActiveText : T.pillText,
        fontWeight: 600,
        fontSize: 13,
        cursor: "pointer",
        fontFamily: "'Google Sans', sans-serif",
        transition: "all 0.15s ease",
        whiteSpace: "nowrap",
      }}
    >
      {label}
    </button>
  );

  const sectionCardStyle = {
    background: T.aiBubble,
    border: `1px solid ${T.aiBorder}`,
    borderRadius: 18,
    padding: 18,
    boxShadow: T.shadow,
  };

  const itemCardStyle = {
    background: T.bgSub,
    border: `1px solid ${T.border}`,
    borderRadius: 16,
    padding: 16,
    boxShadow: T.shadow,
  };

  const labelStyle = {
    fontSize: 11,
    fontWeight: 700,
    color: T.textMuted,
    textTransform: "uppercase",
    letterSpacing: ".08em",
    marginBottom: 8,
  };

  const deleteBtnStyle = {
    border: "none",
    borderRadius: 12,
    background: T.danger,
    color: "#fff",
    padding: "10px 14px",
    fontWeight: 700,
    fontSize: 13,
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    fontFamily: "'Google Sans', sans-serif",
  };

  const alertAccent =
    alertModal.type === "success"
      ? T.success
      : alertModal.type === "error"
      ? T.danger
      : T.accent;

  /* ─── LOGIN SCREEN ─── */
  if (!loggedIn) {
    return (
      <div
        style={{
          minHeight: "100vh",
          background: T.bg,
          color: T.text,
          fontFamily: "'Google Sans Text', 'Google Sans', sans-serif",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: 20,
          position: "relative",
        }}
      >
        <style>{`
          * { box-sizing: border-box; }
          @keyframes fadeUp {
            from { opacity: 0; transform: translateY(8px); }
            to { opacity: 1; transform: translateY(0); }
          }
          @keyframes popIn {
            from { opacity: 0; transform: scale(.96) translateY(6px); }
            to { opacity: 1; transform: scale(1) translateY(0); }
          }
          .fade-up { animation: fadeUp .25s ease forwards; }
          .pop-in { animation: popIn .18s ease forwards; }
          .material-icons-round { font-family: 'Material Icons Round'; }
          input::placeholder { color: ${T.textMuted}; }
        `}</style>

        <div
          className="fade-up"
          style={{
            width: "100%",
            maxWidth: 440,
            background: T.aiBubble,
            border: `1px solid ${T.aiBorder}`,
            borderRadius: 22,
            padding: 28,
            boxShadow: T.shadowMd,
          }}
        >
          <div
            style={{
              width: 54,
              height: 54,
              borderRadius: 14,
              background: T.accent,
              color: T.accentText,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontWeight: 800,
              fontSize: 22,
              marginBottom: 18,
            }}
          >
            O
          </div>

          <div
            style={{
              fontSize: 12,
              color: T.textSub,
              fontWeight: 700,
              textTransform: "uppercase",
              letterSpacing: ".08em",
            }}
          >
            Admin access
          </div>

          <h1
            style={{
              margin: "8px 0 10px",
              fontSize: 30,
              fontWeight: 800,
              letterSpacing: "-0.02em",
              color: T.text,
            }}
          >
            Omrali <span style={{ color: T.textSub }}>Admin</span>
          </h1>

          <p
            style={{
              color: T.textSub,
              fontSize: 14,
              lineHeight: 1.7,
              marginBottom: 22,
            }}
          >
            Sign in to manage conversations, users, waitlist entries, contact
            submissions, donor list, and site views.
          </p>

          <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            <input
              placeholder="Username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              style={{
                width: "100%",
                minHeight: 52,
                padding: "14px 16px",
                borderRadius: 14,
                border: `1px solid ${T.inputBorder}`,
                background: T.inputBg,
                color: T.text,
                outline: "none",
                fontSize: 14.5,
                fontFamily: "'Google Sans Text', 'Google Sans', sans-serif",
              }}
            />

            <input
              type="password"
              placeholder="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleLogin()}
              style={{
                width: "100%",
                minHeight: 52,
                padding: "14px 16px",
                borderRadius: 14,
                border: `1px solid ${T.inputBorder}`,
                background: T.inputBg,
                color: T.text,
                outline: "none",
                fontSize: 14.5,
                fontFamily: "'Google Sans Text', 'Google Sans', sans-serif",
              }}
            />

            <button
              onClick={handleLogin}
              style={{
                width: "100%",
                minHeight: 50,
                border: "none",
                borderRadius: 14,
                background: T.accent,
                color: T.accentText,
                fontWeight: 700,
                fontSize: 14,
                cursor: "pointer",
                fontFamily: "'Google Sans', sans-serif",
              }}
            >
              Login to Dashboard
            </button>
          </div>
        </div>

        {/* ALERT MODAL */}
        {alertModal.open && (
          <div
            onClick={closeAlert}
            style={{
              position: "fixed",
              inset: 0,
              background: "rgba(0,0,0,0.45)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: 20,
              zIndex: 9999,
            }}
          >
            <div
              className="pop-in"
              onClick={(e) => e.stopPropagation()}
              style={{
                width: "100%",
                maxWidth: 440,
                background: T.aiBubble,
                border: `1px solid ${T.aiBorder}`,
                borderRadius: 22,
                padding: 22,
                boxShadow: T.shadowMd,
              }}
            >
              <div
                style={{
                  width: 48,
                  height: 48,
                  borderRadius: 14,
                  background: `${alertAccent}22`,
                  color: alertAccent,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  marginBottom: 14,
                }}
              >
                <Icon>
                  {alertModal.type === "success"
                    ? "check_circle"
                    : alertModal.type === "error"
                    ? "error"
                    : "info"}
                </Icon>
              </div>

              <h3 style={{ margin: 0, fontSize: 22, fontWeight: 800 }}>
                {alertModal.title}
              </h3>

              <p
                style={{
                  marginTop: 10,
                  color: T.textSub,
                  fontSize: 14,
                  lineHeight: 1.7,
                }}
              >
                {alertModal.message}
              </p>

              <button
                onClick={closeAlert}
                style={{
                  marginTop: 18,
                  width: "100%",
                  minHeight: 48,
                  border: "none",
                  borderRadius: 14,
                  background: T.accent,
                  color: T.accentText,
                  fontWeight: 700,
                  fontSize: 14,
                  cursor: "pointer",
                  fontFamily: "'Google Sans', sans-serif",
                }}
              >
                Okay
              </button>
            </div>
          </div>
        )}
      </div>
    );
  }

  /* ─── DASHBOARD ─── */
  return (
    <div
      style={{
        minHeight: "100vh",
        background: T.bg,
        color: T.text,
        fontFamily: "'Google Sans Text', 'Google Sans', sans-serif",
        position: "relative",
      }}
    >
      <style>{`
        * { box-sizing: border-box; }

        @keyframes fadeUp {
          from { opacity: 0; transform: translateY(8px); }
          to { opacity: 1; transform: translateY(0); }
        }

        @keyframes popIn {
          from { opacity: 0; transform: scale(.96) translateY(6px); }
          to { opacity: 1; transform: scale(1) translateY(0); }
        }

        .fade-up { animation: fadeUp .25s ease forwards; }
        .pop-in { animation: popIn .18s ease forwards; }

        .material-icons-round { font-family: 'Material Icons Round'; }

        .admin-scroll::-webkit-scrollbar {
          width: 6px;
          height: 6px;
        }

        .admin-scroll::-webkit-scrollbar-thumb {
          background: ${T.border};
          border-radius: 10px;
        }

        .hover-card {
          transition: transform .12s ease, border-color .12s ease, background .12s ease;
        }

        .hover-card:hover {
          transform: translateY(-2px);
          border-color: ${T.borderStrong};
          background: ${T.surface};
        }

        .soft-btn {
          transition: transform .12s ease, opacity .12s ease;
        }

        .soft-btn:hover {
          transform: translateY(-1px);
          opacity: .9;
        }

        @media (max-width: 768px) {
          .top-grid {
            grid-template-columns: 1fr !important;
          }
        }
      `}</style>

      <div
        style={{
          maxWidth: 1400,
          margin: "0 auto",
          padding: "22px 18px 60px",
        }}
      >
        {/* Header */}
        <div
          className="fade-up"
          style={{
            ...sectionCardStyle,
            marginBottom: 18,
            padding: 22,
          }}
        >
          <div
            className="top-grid"
            style={{
              display: "grid",
              gridTemplateColumns: "1.3fr auto",
              gap: 16,
              alignItems: "center",
            }}
          >
            <div>
              <div
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 8,
                  background: T.surface,
                  border: `1px solid ${T.border}`,
                  color: T.textSub,
                  borderRadius: 999,
                  padding: "6px 12px",
                  fontSize: 11,
                  fontWeight: 700,
                  letterSpacing: ".08em",
                  textTransform: "uppercase",
                  marginBottom: 12,
                }}
              >
                <Icon style={{ fontSize: 16 }}>shield</Icon>
                Admin Dashboard
              </div>

              <h1
                style={{
                  fontSize: "clamp(28px, 5vw, 40px)",
                  lineHeight: 1.08,
                  fontWeight: 900,
                  margin: 0,
                  letterSpacing: "-0.02em",
                }}
              >
                Manage <span style={{ color: T.textSub }}>Omrali AI</span>
              </h1>

              <p
                style={{
                  marginTop: 12,
                  color: T.textSub,
                  fontSize: 14,
                  lineHeight: 1.7,
                  maxWidth: 700,
                }}
              >
                Review user activity, conversations, beta interest, donors,
                contact messages, and traffic data from one place.
              </p>
            </div>

            <div
              style={{
                display: "flex",
                gap: 10,
                flexWrap: "wrap",
                justifyContent: "flex-end",
              }}
            >
              <button
                onClick={fetchData}
                className="soft-btn"
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 8,
                  border: "none",
                  borderRadius: 12,
                  background: T.accent,
                  color: T.accentText,
                  padding: "12px 16px",
                  fontWeight: 700,
                  fontSize: 14,
                  cursor: "pointer",
                  fontFamily: "'Google Sans', sans-serif",
                }}
              >
                <Icon style={{ fontSize: 18 }}>
                  {loading ? "hourglass_empty" : "refresh"}
                </Icon>
                {loading ? "Refreshing..." : "Refresh Data"}
              </button>

              <button
                onClick={() => {
                  setLoggedIn(false);
                  setUsername("");
                  setPassword("");
                }}
                className="soft-btn"
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 8,
                  border: `1px solid ${T.border}`,
                  borderRadius: 12,
                  background: T.bgSub,
                  color: T.text,
                  padding: "12px 16px",
                  fontWeight: 700,
                  fontSize: 14,
                  cursor: "pointer",
                  fontFamily: "'Google Sans', sans-serif",
                }}
              >
                <Icon style={{ fontSize: 18 }}>logout</Icon>
                Logout
              </button>
            </div>
          </div>
        </div>

        {/* Stats */}
        <div
          className="fade-up"
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
            gap: 14,
            marginBottom: 18,
          }}
        >
          {[
            { label: "Conversations", value: conversations.length, icon: "chat" },
            { label: "Users", value: users.length, icon: "group" },
            { label: "Waitlist", value: waitlist.length, icon: "bolt" },
            { label: "Contacts", value: contacts.length, icon: "mail" },
            { label: "Donors", value: donors.length, icon: "volunteer_activism" },
            { label: "Views", value: views.length, icon: "visibility" },
          ].map((stat) => (
            <div key={stat.label} style={itemCardStyle} className="hover-card">
              <div
                style={{
                  width: 42,
                  height: 42,
                  borderRadius: 12,
                  background: T.surface,
                  border: `1px solid ${T.border}`,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  marginBottom: 10,
                }}
              >
                <Icon style={{ color: T.text }}>{stat.icon}</Icon>
              </div>
              <div
                style={{
                  fontSize: 11,
                  color: T.textMuted,
                  fontWeight: 700,
                  textTransform: "uppercase",
                  letterSpacing: ".08em",
                }}
              >
                {stat.label}
              </div>
              <div
                style={{
                  marginTop: 6,
                  fontSize: 28,
                  fontWeight: 800,
                  letterSpacing: "-0.02em",
                }}
              >
                {stat.value}
              </div>
            </div>
          ))}
        </div>

        {/* Tabs */}
        <div
          className="fade-up admin-scroll"
          style={{
            ...sectionCardStyle,
            marginBottom: 18,
            padding: 14,
            overflowX: "auto",
          }}
        >
          <div
            style={{
              display: "flex",
              gap: 10,
              minWidth: "max-content",
              flexWrap: "nowrap",
            }}
          >
            {tabButton("history", "Prompt History")}
            {tabButton("users", "Users")}
            {tabButton("waitlist", "Waitlist")}
            {tabButton("contact", "Contact Us")}
            {tabButton("donors", "Donor List")}
            {tabButton("views", "Site Views")}
          </div>
        </div>

        {/* Content */}
        <div className="fade-up" style={sectionCardStyle}>
          {/* HISTORY */}
          {activeTab === "history" && (
            <div>
              <div style={labelStyle}>Prompt history</div>
              <h2 style={{ fontSize: 24, margin: "0 0 18px", fontWeight: 800 }}>
                Conversations
              </h2>

              <div style={{ display: "grid", gap: 14 }}>
                {conversations.length === 0 ? (
                  <div style={{ color: T.textSub, fontSize: 14 }}>
                    No conversations found.
                  </div>
                ) : (
                  conversations.map((c) => (
                    <div key={c.id} style={itemCardStyle} className="hover-card">
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          gap: 12,
                          flexWrap: "wrap",
                          marginBottom: 10,
                        }}
                      >
                        <div style={{ fontWeight: 700, color: T.text }}>
                          {c.name || "Unknown"}{" "}
                          <span style={{ color: T.textSub, fontWeight: 500 }}>
                            ({c.email || "Guest"})
                          </span>
                        </div>
                        <div style={{ fontSize: 12, color: T.textMuted }}>
                          {formatDate(c.timestamp)}
                        </div>
                      </div>

                      <div style={{ marginBottom: 10 }}>
                        <div style={labelStyle}>User prompt</div>
                        <div style={{ color: T.text, lineHeight: 1.7, fontSize: 14 }}>
                          {c.message}
                        </div>
                      </div>

                      <div style={{ marginBottom: 14 }}>
                        <div style={labelStyle}>AI response</div>
                        <div style={{ color: T.textSub, lineHeight: 1.7, fontSize: 14 }}>
                          {c.response}
                        </div>
                      </div>

                      <button
                        onClick={() =>
                          handleDelete(
                            "conversations",
                            c.id,
                            setConversations,
                            "conversation"
                          )
                        }
                        className="soft-btn"
                        style={deleteBtnStyle}
                      >
                        <Icon style={{ fontSize: 18 }}>delete</Icon>
                        Delete
                      </button>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}

          {/* USERS */}
          {activeTab === "users" && (
            <div>
              <div style={labelStyle}>Registered users</div>
              <h2 style={{ fontSize: 24, margin: "0 0 18px", fontWeight: 800 }}>
                Users
              </h2>

              <div style={{ display: "grid", gap: 12 }}>
                {users.length === 0 ? (
                  <div style={{ color: T.textSub, fontSize: 14 }}>No users found.</div>
                ) : (
                  users.map((u, i) => (
                    <div key={u.id || u.email || i} style={itemCardStyle} className="hover-card">
                      <div style={{ fontWeight: 700, color: T.text }}>
                        {u.name || "Unnamed User"}
                      </div>
                      <div style={{ color: T.textSub, marginTop: 4, fontSize: 14 }}>
                        {u.email || "No email"}
                      </div>

                      <div style={{ marginTop: 14 }}>
                        <button
                          onClick={() =>
                            handleDelete("users", u.id, setUsers, "user")
                          }
                          className="soft-btn"
                          style={deleteBtnStyle}
                        >
                          <Icon style={{ fontSize: 18 }}>delete</Icon>
                          Delete
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}

          {/* WAITLIST */}
          {activeTab === "waitlist" && (
            <div>
              <div style={labelStyle}>Beta interest</div>
              <h2 style={{ fontSize: 24, margin: "0 0 18px", fontWeight: 800 }}>
                Waitlist
              </h2>

              <div style={{ display: "grid", gap: 12 }}>
                {waitlist.length === 0 ? (
                  <div style={{ color: T.textSub, fontSize: 14 }}>
                    No waitlist entries found.
                  </div>
                ) : (
                  waitlist.map((w, i) => (
                    <div key={w.id || w.email || i} style={itemCardStyle} className="hover-card">
                      <div style={{ fontWeight: 700, color: T.text }}>
                        {w.name || "Unnamed"}
                      </div>
                      <div style={{ color: T.textSub, marginTop: 4, fontSize: 14 }}>
                        {w.email || "No email"}
                      </div>

                      <div style={{ marginTop: 14 }}>
                        <button
                          onClick={() =>
                            handleDelete("waitlist", w.id, setWaitlist, "waitlist entry")
                          }
                          className="soft-btn"
                          style={deleteBtnStyle}
                        >
                          <Icon style={{ fontSize: 18 }}>delete</Icon>
                          Delete
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}

          {/* CONTACT */}
          {activeTab === "contact" && (
            <div>
              <div style={labelStyle}>User inquiries</div>
              <h2 style={{ fontSize: 24, margin: "0 0 18px", fontWeight: 800 }}>
                Contact Messages
              </h2>

              <div style={{ display: "grid", gap: 12 }}>
                {contacts.filter((c) => !c.checked).length > 0 && (
                  <>
                    <div style={{ fontSize: 13, fontWeight: 700, color: T.textSub }}>
                      Pending Messages
                    </div>

                    {contacts
                      .filter((c) => !c.checked)
                      .map((c) => (
                        <div key={c.id} style={itemCardStyle} className="hover-card">
                          <div
                            style={{
                              display: "flex",
                              justifyContent: "space-between",
                              gap: 12,
                              flexWrap: "wrap",
                              marginBottom: 10,
                            }}
                          >
                            <div style={{ fontWeight: 700, color: T.text }}>
                              {c.name}{" "}
                              <span style={{ color: T.textSub, fontWeight: 500 }}>
                                ({c.email})
                              </span>
                            </div>
                            <div style={{ fontSize: 12, color: T.textMuted }}>
                              {formatDate(c.timestamp)}
                            </div>
                          </div>

                          <div
                            style={{
                              color: T.text,
                              fontSize: 14,
                              lineHeight: 1.7,
                              marginBottom: 14,
                            }}
                          >
                            {c.message}
                          </div>

                          <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                            <button
                              onClick={() => markContactChecked(c.id)}
                              className="soft-btn"
                              style={{
                                border: "none",
                                borderRadius: 12,
                                background: T.accent,
                                color: T.accentText,
                                padding: "10px 14px",
                                fontWeight: 700,
                                fontSize: 13,
                                cursor: "pointer",
                                display: "inline-flex",
                                alignItems: "center",
                                gap: 8,
                                fontFamily: "'Google Sans', sans-serif",
                              }}
                            >
                              <Icon style={{ fontSize: 18 }}>check_circle</Icon>
                              Mark as Checked
                            </button>

                            <button
                              onClick={() =>
                                handleDelete("contact", c.id, setContacts, "contact message")
                              }
                              className="soft-btn"
                              style={deleteBtnStyle}
                            >
                              <Icon style={{ fontSize: 18 }}>delete</Icon>
                              Delete
                            </button>
                          </div>
                        </div>
                      ))}
                  </>
                )}

                {contacts.filter((c) => c.checked).length > 0 && (
                  <>
                    <div
                      style={{
                        fontSize: 13,
                        fontWeight: 700,
                        color: T.textSub,
                        marginTop: 10,
                      }}
                    >
                      Checked Messages
                    </div>

                    {contacts
                      .filter((c) => c.checked)
                      .map((c) => (
                        <div
                          key={c.id}
                          style={{
                            ...itemCardStyle,
                            border: `1px solid ${T.success}55`,
                            background: `${T.success}10`,
                          }}
                        >
                          <div
                            style={{
                              display: "flex",
                              justifyContent: "space-between",
                              gap: 12,
                              flexWrap: "wrap",
                              marginBottom: 10,
                            }}
                          >
                            <div style={{ fontWeight: 700, color: T.text }}>
                              {c.name}{" "}
                              <span style={{ color: T.textSub, fontWeight: 500 }}>
                                ({c.email})
                              </span>
                            </div>
                            <div style={{ fontSize: 12, color: T.textMuted }}>
                              {formatDate(c.timestamp)}
                            </div>
                          </div>

                          <div
                            style={{
                              color: T.textSub,
                              fontSize: 14,
                              lineHeight: 1.7,
                              marginBottom: 14,
                            }}
                          >
                            {c.message}
                          </div>

                          <button
                            onClick={() =>
                              handleDelete("contact", c.id, setContacts, "contact message")
                            }
                            className="soft-btn"
                            style={deleteBtnStyle}
                          >
                            <Icon style={{ fontSize: 18 }}>delete</Icon>
                            Delete
                          </button>
                        </div>
                      ))}
                  </>
                )}

                {contacts.length === 0 && (
                  <div style={{ color: T.textSub, fontSize: 14 }}>
                    No contact submissions found.
                  </div>
                )}
              </div>
            </div>
          )}

          {/* DONORS */}
          {activeTab === "donors" && (
            <div>
              <div style={labelStyle}>Supporters</div>
              <h2 style={{ fontSize: 24, margin: "0 0 18px", fontWeight: 800 }}>
                Donor List
              </h2>

              <div style={{ display: "grid", gap: 12 }}>
                {donors.length === 0 ? (
                  <div style={{ color: T.textSub, fontSize: 14 }}>
                    No donors found.
                  </div>
                ) : (
                  donors.map((d, i) => (
                    <div key={d.id || d.email || i} style={itemCardStyle} className="hover-card">
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          gap: 12,
                          flexWrap: "wrap",
                          marginBottom: 10,
                        }}
                      >
                        <div style={{ fontWeight: 700, color: T.text }}>
                          {d.name || "Anonymous Donor"}
                        </div>
                        <div style={{ fontSize: 12, color: T.textMuted }}>
                          {formatDate(d.timestamp)}
                        </div>
                      </div>

                      <div style={{ color: T.textSub, fontSize: 14, lineHeight: 1.8 }}>
                        <div><strong style={{ color: T.text }}>Email:</strong> {d.email || "No email"}</div>
                        <div><strong style={{ color: T.text }}>Amount:</strong> {d.amount || d.donation || "N/A"}</div>
                        <div><strong style={{ color: T.text }}>Currency:</strong> {d.currency || "N/A"}</div>
                        <div><strong style={{ color: T.text }}>Message:</strong> {d.message || "No message"}</div>
                        <div><strong style={{ color: T.text }}>Payment ID:</strong> {d.paymentId || d.transactionId || "N/A"}</div>
                      </div>

                      <div style={{ marginTop: 14 }}>
                        <button
                          onClick={() =>
                            handleDelete("donors", d.id, setDonors, "donor entry")
                          }
                          className="soft-btn"
                          style={deleteBtnStyle}
                        >
                          <Icon style={{ fontSize: 18 }}>delete</Icon>
                          Delete
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}

          {/* VIEWS */}
          {activeTab === "views" && (
            <div>
              <div style={labelStyle}>Traffic</div>
              <h2 style={{ fontSize: 24, margin: "0 0 8px", fontWeight: 800 }}>
                Site Views
              </h2>
              <p style={{ color: T.textSub, fontSize: 14, marginBottom: 18 }}>
                Total Views: <strong style={{ color: T.text }}>{views.length}</strong>
              </p>

              <div style={{ display: "grid", gap: 12 }}>
                {views.length === 0 ? (
                  <div style={{ color: T.textSub, fontSize: 14 }}>No views found.</div>
                ) : (
                  views.map((view) => (
                    <div key={view.id} style={itemCardStyle} className="hover-card">
                      <div style={{ fontWeight: 700, color: T.text }}>
                        View ID: {view.id}
                      </div>
                      <div style={{ color: T.textSub, marginTop: 6, fontSize: 14 }}>
                        {formatDate(view.timestamp)}
                      </div>
                      <div style={{ color: T.textSub, marginTop: 4, fontSize: 14 }}>
                        Visited by: {view.email || "Anonymous"}
                      </div>

                      <div style={{ marginTop: 14 }}>
                        <button
                          onClick={() =>
                            handleDelete("views", view.id, setViews, "view record")
                          }
                          className="soft-btn"
                          style={deleteBtnStyle}
                        >
                          <Icon style={{ fontSize: 18 }}>delete</Icon>
                          Delete
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div
          className="fade-up"
          style={{
            marginTop: 18,
            background: T.bgSub,
            border: `1px solid ${T.border}`,
            borderRadius: 18,
            padding: 18,
            boxShadow: T.shadow,
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              gap: 10,
              flexWrap: "wrap",
              alignItems: "center",
            }}
          >
            <div style={{ fontSize: 13, color: T.textSub }}>
              Omrali <span style={{ color: T.text }}>Admin Panel</span>
            </div>
            <div style={{ fontSize: 12, color: T.textMuted }}>
              © 2026 Omrali AI. Internal dashboard.
            </div>
          </div>
        </div>
      </div>

      {/* GLOBAL ALERT MODAL */}
      {alertModal.open && (
        <div
          onClick={closeAlert}
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.45)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 20,
            zIndex: 9999,
          }}
        >
          <div
            className="pop-in"
            onClick={(e) => e.stopPropagation()}
            style={{
              width: "100%",
              maxWidth: 460,
              background: T.aiBubble,
              border: `1px solid ${T.aiBorder}`,
              borderRadius: 22,
              padding: 22,
              boxShadow: T.shadowMd,
            }}
          >
            <div
              style={{
                width: 48,
                height: 48,
                borderRadius: 14,
                background: `${alertAccent}22`,
                color: alertAccent,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                marginBottom: 14,
              }}
            >
              <Icon>
                {alertModal.type === "success"
                  ? "check_circle"
                  : alertModal.type === "error"
                  ? "error"
                  : "info"}
              </Icon>
            </div>

            <h3 style={{ margin: 0, fontSize: 22, fontWeight: 800 }}>
              {alertModal.title}
            </h3>

            <p
              style={{
                marginTop: 10,
                color: T.textSub,
                fontSize: 14,
                lineHeight: 1.7,
              }}
            >
              {alertModal.message}
            </p>

            <button
              onClick={closeAlert}
              style={{
                marginTop: 18,
                width: "100%",
                minHeight: 48,
                border: "none",
                borderRadius: 14,
                background: T.accent,
                color: T.accentText,
                fontWeight: 700,
                fontSize: 14,
                cursor: "pointer",
                fontFamily: "'Google Sans', sans-serif",
              }}
            >
              Okay
            </button>
          </div>
        </div>
      )}

      {/* GLOBAL CONFIRM MODAL */}
      {confirmModal.open && (
        <div
          onClick={closeConfirm}
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.45)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 20,
            zIndex: 9999,
          }}
        >
          <div
            className="pop-in"
            onClick={(e) => e.stopPropagation()}
            style={{
              width: "100%",
              maxWidth: 480,
              background: T.aiBubble,
              border: `1px solid ${T.aiBorder}`,
              borderRadius: 22,
              padding: 22,
              boxShadow: T.shadowMd,
            }}
          >
            <div
              style={{
                width: 48,
                height: 48,
                borderRadius: 14,
                background: `${T.danger}22`,
                color: T.danger,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                marginBottom: 14,
              }}
            >
              <Icon>warning</Icon>
            </div>

            <h3 style={{ margin: 0, fontSize: 22, fontWeight: 800 }}>
              {confirmModal.title}
            </h3>

            <p
              style={{
                marginTop: 10,
                color: T.textSub,
                fontSize: 14,
                lineHeight: 1.7,
              }}
            >
              {confirmModal.message}
            </p>

            <div
              style={{
                marginTop: 18,
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: 10,
              }}
            >
              <button
                onClick={closeConfirm}
                style={{
                  minHeight: 48,
                  border: `1px solid ${T.border}`,
                  borderRadius: 14,
                  background: T.bgSub,
                  color: T.text,
                  fontWeight: 700,
                  fontSize: 14,
                  cursor: "pointer",
                  fontFamily: "'Google Sans', sans-serif",
                }}
              >
                Cancel
              </button>

              <button
                onClick={() => {
                  if (confirmModal.onConfirm) confirmModal.onConfirm();
                }}
                style={{
                  minHeight: 48,
                  border: "none",
                  borderRadius: 14,
                  background: T.danger,
                  color: "#fff",
                  fontWeight: 700,
                  fontSize: 14,
                  cursor: "pointer",
                  fontFamily: "'Google Sans', sans-serif",
                }}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}